import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { track } from "@/lib/analytics";

const DISMISS_KEY = "harmonix_pwa_dismissed_at";
const DISMISS_DAYS = 7;

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * InstallPrompt — subtle banner inviting users to install the PWA.
 * - On Chromium: listens for `beforeinstallprompt` and uses it on tap.
 * - On iOS Safari: shows an Add-to-Home-Screen instruction (iOS does not fire the event).
 * - Hidden entirely when already installed, or dismissed within last 7 days.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installedToast, setInstalledToast] = useState(false);
  const iOS = typeof window !== "undefined" && isIOS();

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // small delay so banner animates in after first paint
      setTimeout(() => setVisible(true), 1200);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      track("PWA Installed");
      setInstalledToast(true);
      setVisible(false);
      setDeferred(null);
      setTimeout(() => setInstalledToast(false), 5000);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS has no beforeinstallprompt — show the hint banner directly after a delay
    if (iOS) {
      setTimeout(() => setVisible(true), 3500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [iOS]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome !== "accepted") {
        dismiss();
      }
    } catch (_e) {}
    setDeferred(null);
    setVisible(false);
  };

  if (installedToast) {
    return (
      <div
        className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-auto z-50 fade-up"
        data-testid="pwa-installed-toast"
      >
        <div
          className="card-warm px-4 py-3 flex items-center gap-3 shadow-lg"
          style={{ background: "#E8F0EC", borderColor: "#2D5A4C" }}
        >
          <Download size={16} style={{ color: "#2D5A4C" }} />
          <p className="text-sm" style={{ color: "#1C2826" }}>
            <strong>Installed!</strong> Launch Harmonix from your home screen anytime.
          </p>
        </div>
      </div>
    );
  }

  if (!visible) return null;
  if (!iOS && !deferred) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-[380px] z-50 fade-up"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Install Harmonix to home screen"
    >
      <div
        className="card-warm p-4 flex items-start gap-3 shadow-xl"
        style={{ background: "#FFFFFF", borderColor: "#E5DEC9" }}
      >
        <div
          className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
          style={{ background: "#2D5A4C" }}
        >
          <Download size={18} color="white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif-display text-lg font-semibold leading-tight" style={{ color: "#1C2826" }}>
            Install Harmonix
          </p>
          {iOS ? (
            <p className="mt-1 text-sm leading-relaxed" style={{ color: "#5C6B66" }}>
              Tap <Share size={14} className="inline mx-1 mb-0.5" style={{ color: "#2D5A4C" }} /> then
              <strong> Add to Home Screen</strong> for a one-tap launch.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm leading-relaxed" style={{ color: "#5C6B66" }}>
                Add Harmonix to your home screen for a one-tap launch, works offline, no app-store needed.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  data-testid="pwa-install-btn"
                  onClick={install}
                  className="btn-brand rounded-full h-9 px-4 text-sm font-semibold"
                >
                  Install
                </button>
                <button
                  data-testid="pwa-dismiss-btn"
                  onClick={dismiss}
                  className="rounded-full h-9 px-3 text-sm"
                  style={{ color: "#5C6B66" }}
                >
                  Not now
                </button>
              </div>
            </>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="p-1.5 rounded-full hover:bg-[#F4EFE6] transition-colors shrink-0"
          data-testid="pwa-close-btn"
        >
          <X size={16} style={{ color: "#5C6B66" }} />
        </button>
      </div>
    </div>
  );
}
